import Attachment from '../../buttons/attachment-icon.svg';
import { useState, useEffect } from 'react';
import { fetchAuthenticatedImage } from '../services/authenticatedImageService';

// Track revoked URLs to make cleanup idempotent
const revokedChatUrls = new Set();

export default function ChatMessage({
    sEvent,
    loggedInUser,
    getUserDisplayName,
    formatDate,
    handleImageMessage,
    matrixClient,
    onImageClick
}) {
    const isOwnMessage = sEvent.sender === loggedInUser.synapse_user_id;
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);

    const isImage = sEvent.content && sEvent.content.msgtype === "m.image";
    const isFile = sEvent.content && sEvent.content.msgtype === "m.file";

    // Fetch authenticated image blob URL when component mounts or URL changes
    useEffect(() => {
        const loadImage = async () => {
            if (isImage && sEvent.content?.url && matrixClient) {
                setImageLoading(true);
                setImageLoadFailed(false);
                
                try {
                    const result = await fetchAuthenticatedImage(sEvent.content.url, matrixClient, false);
                    setImageUrl(result.url);
                } catch (error) {
                    setImageLoadFailed(true);
                } finally {
                    setImageLoading(false);
                }
            }
        };

        loadImage();

        // Cleanup blob URL when component unmounts (idempotent)
        return () => {
            if (imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('blob:')) {
                try {
                    if (!revokedChatUrls.has(imageUrl)) {
                        URL.revokeObjectURL(imageUrl);
                        revokedChatUrls.add(imageUrl);
                    }
                } catch (error) {
                    console.warn('Error revoking object URL:', error);
                }
            }
        };
    }, [isImage, sEvent.content?.url, matrixClient]);

    const handleFileClick = (content) => {

        if (content.msgtype === "m.image") {
            // For images, pass MXC URL to onImageClick for authenticated fetching
            onImageClick(null, content.body, content.url);
        } else {
            // For other files, use the existing download functionality
            handleImageMessage(content.url, content.body);
        }
    };


    return (
        <div className="flex flex-col mb-2">
            {isOwnMessage ? (
                <div className="flex justify-start">
                    <div className="max-w-[85%]">
                        <div className="text-[#2c2c2c] text-sm font-semibold leading-5 m-0 ml-2.5 mt-2.5">
                            {getUserDisplayName(sEvent.sender)}
                        </div>
                        <div className="text-[#989898] text-xs font-medium leading-2.5 m-0 ml-2.5 mb-1.25">
                            {formatDate(sEvent.origin_server_ts)}
                        </div>
                        {sEvent.content && sEvent.content.msgtype === "m.text" && (
                            <div className="bg-[#0b6d76] rounded-tr-lg rounded-b-lg m-0 ml-2.5 flex-shrink-0">
                                <div className="p-3 text-left leading-5 text-white text-xs font-medium">
                                    <p className="m-0">{sEvent.content.body}</p>
                                </div>
                            </div>
                        )}
                        {(isImage || isFile) && (
                            <div className="flex items-center justify-end">
                                {isImage ? (
                                    <div className="relative group">
                                        {/* Debug logs before rendering image */}

                                        {/* Show loading state */}
                                        {imageLoading && (
                                            <div className="w-32 h-32 bg-gray-200 rounded-lg flex flex-col items-center justify-center">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                                                <p className="text-xs text-gray-500">Loading...</p>
                                            </div>
                                        )}

                                        {/* Only render img if not loading, not failed, and we have a blob URL */}
                                        {!imageLoading && !imageLoadFailed && imageUrl && (
                                            <>
                                                <img
                                                    src={imageUrl}
                                                    alt={sEvent.content.body}
                                                    className="w-32 h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                                    onClick={() => handleFileClick(sEvent.content)}
                                                    onError={(e) => {
                                                        setImageLoadFailed(true);
                                                    }}
                                                    onLoad={(e) => {
                                                        setImageLoadFailed(false);
                                                    }}
                                                />
                                            </>
                                        )}

                                        {/* Show error state if image failed to load */}
                                        {imageLoadFailed && (
                                            <div className="flex items-center space-x-2 text-red-500">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                                </svg>
                                                <span className="text-xs">Failed to load image</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <img src={Attachment} alt="File" className="mr-2 w-4 h-4" />
                                        <button
                                            onClick={() => handleFileClick(sEvent.content)}
                                            className="bg-[#17d5e6] rounded-tr-lg rounded-b-lg m-0 mr-2.5 flex-shrink-0 p-3 text-left"
                                        >
                                            <p className="text-white text-xs font-medium m-0 truncate max-w-[200px]">
                                                {sEvent.content.body}
                                            </p>
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex-1"></div>
                </div>
            ) : (
                <div className="flex justify-start">
                    <div className="flex-1"></div>
                    <div className="max-w-[85%]">
                        <div className="text-[#2c2c2c] text-sm font-semibold leading-5 text-right m-0 mr-2.5 mt-2.5">
                            {getUserDisplayName(sEvent.sender)}
                        </div>
                        <div className="text-[#989898] text-xs font-medium leading-2.5 text-right m-0 mr-2.5 mb-1.25">
                            {formatDate(sEvent.origin_server_ts)}
                        </div>
                        {sEvent.content && sEvent.content.msgtype === "m.text" && (
                            <div className="bg-[#616161] rounded-tl-lg rounded-b-lg m-0 mr-2.5 flex-shrink-0">
                                <div className="p-3 text-left leading-5 text-white text-xs font-medium">
                                    <p className="m-0">{sEvent.content.body}</p>
                                </div>
                            </div>
                        )}
                        {(isImage || isFile) && (
                            <div className="flex items-center">
                                {isImage ? (
                                    <div className="relative group">
                                        {/* Debug logs before rendering image */}
                                        
                                        {/* Show loading state */}
                                        {imageLoading && (
                                            <div className="w-32 h-32 bg-gray-200 rounded-lg flex flex-col items-center justify-center">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                                                <p className="text-xs text-gray-500">Loading...</p>
                                            </div>
                                        )}
                                        
                                        {/* Only render img if not loading, not failed, and we have a blob URL */}
                                        {!imageLoading && !imageLoadFailed && imageUrl && (
                                            <>
                                                    <img 
                                                    src={imageUrl}
                                                    alt={sEvent.content.body}
                                                    className="w-32 h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                                    onClick={() => handleFileClick(sEvent.content)}
                                                    onError={(e) => {
                                                        setImageLoadFailed(true);
                                                    }}
                                                    onLoad={(e) => {
                                                    }}
                                                />
                                            </>
                                        )}
                                        
                                        {/* Show error state if image failed to load */}
                                        {imageLoadFailed && (
                                            <div className="flex items-center space-x-2 text-red-500">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                                </svg>
                                                <span className="text-xs">Failed to load image</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <img src={Attachment} alt="File" className="mr-2 w-4 h-4" />
                                        <button 
                                            onClick={() => handleFileClick(sEvent.content)}
                                            className="bg-[#b0afaf] rounded-tl-lg rounded-b-lg m-0 mr-2.5 flex-shrink-0 p-3 text-left"
                                        >
                                            <p className="m-0 text-white text-xs font-medium leading-5">
                                                {sEvent.content.body}
                                            </p>
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
